// Parsing for the in-app Help Center articles (eventlive.pro/help/<slug>.md).
// Rendering is in Markdown.js.

import { headingAnchor } from "./helpTopics";

// The .md endpoint starts with "# Title", "> description" and Source/Section/
// Updated lines. Returns { title, description, body }.
export function splitHelpMarkdown(md) {
  const lines = String(md || "")
    .replace(/\r\n/g, "\n")
    .split("\n");
  let title = "";
  let description = "";
  let i = 0;
  while (i < lines.length && !lines[i].trim()) i++;
  if (/^#\s+/.test(lines[i] || "")) {
    title = lines[i].replace(/^#\s+/, "").trim();
    i++;
  }
  while (i < lines.length && !lines[i].trim()) i++;
  if (/^>\s?/.test(lines[i] || "")) {
    description = lines[i].replace(/^>\s?/, "").trim();
    i++;
  }
  while (
    i < lines.length &&
    (!lines[i].trim() || /^(Source|Section|Updated):\s/.test(lines[i].trim()))
  ) {
    i++;
  }
  return { title, description, body: lines.slice(i).join("\n") };
}

export function parseBlocks(md) {
  const lines = String(md || "")
    .replace(/\r\n/g, "\n")
    .split("\n");
  const blocks = [];
  let para = [];
  let list = null;
  const flushPara = () => {
    if (para.length) blocks.push({ type: "p", text: para.join(" ").trim() });
    para = [];
  };
  const flushList = () => {
    if (list) blocks.push(list);
    list = null;
  };
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (/^```/.test(line)) {
      flushPara();
      flushList();
      const code = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim()))
        code.push(lines[i++]);
      blocks.push({ type: "code", text: code.join("\n") });
      continue;
    }
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushPara();
      flushList();
      blocks.push({
        type: "h",
        level: heading[1].length,
        text: heading[2].trim(),
      });
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
      flushPara();
      flushList();
      blocks.push({ type: "hr" });
      continue;
    }
    const image = line.match(/^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)$/);
    if (image) {
      flushPara();
      flushList();
      blocks.push({ type: "img", alt: image[1], src: image[2] });
      continue;
    }
    const bullet = raw.match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/);
    if (bullet) {
      flushPara();
      const ordered = /\d/.test(bullet[2]);
      const nested = bullet[1].length >= 2;
      if (!list || (list.ordered !== ordered && !nested)) {
        flushList();
        list = { type: "list", ordered, items: [] };
      }
      list.items.push({
        text: bullet[3].trim(),
        nested,
        number: parseInt(bullet[2], 10),
      });
      continue;
    }
    if (/^>\s?/.test(line)) {
      flushPara();
      flushList();
      blocks.push({ type: "quote", text: line.replace(/^>\s?/, "") });
      continue;
    }
    if (list && /^\s{2,}\S/.test(raw) && list.items.length) {
      list.items[list.items.length - 1].text += ` ${line}`;
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return blocks;
}

// The start of what a help tip points to, for the preview in the help sheet:
// the section's heading and the blocks under it, or the article's first
// blocks when the tip has no section (or the heading was renamed). Images and
// rules are left out; `max` blocks at most (the preview fades out anyway).
export function previewBlocks(md, section, max = 4) {
  const blocks = parseBlocks(md).filter(
    (block) => block.type !== "img" && block.type !== "hr"
  );
  const anchor = section ? headingAnchor(section) : null;
  const start = anchor
    ? blocks.findIndex(
        (block) => block.type === "h" && headingAnchor(block.text) === anchor
      )
    : -1;
  const out = [];
  if (start >= 0) {
    const { level } = blocks[start];
    out.push(blocks[start]);
    for (let i = start + 1; i < blocks.length && out.length < max; i++) {
      if (blocks[i].type === "h" && blocks[i].level <= level) break;
      out.push(blocks[i]);
    }
    return out;
  }
  for (let i = 0; i < blocks.length && out.length < max; i++) {
    if (blocks[i].type === "h" && out.length) break;
    out.push(blocks[i]);
  }
  return out;
}

// Inline: ***x***, **x**, *x* / _x_, `x`, [text](url), <https://…>.
// (No lookbehind: older Hermes builds don't support it.)
const INLINE =
  /(\*\*\*([^*]+)\*\*\*|\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|<(https?:\/\/[^>\s]+)>|(^|[\s(])[*_]([^*_\n]+)[*_](?=[\s).,:;!?]|$))/g;

export function parseInline(text) {
  const parts = [];
  let last = 0;
  let m;
  INLINE.lastIndex = 0;
  const s = String(text || "");
  while ((m = INLINE.exec(s))) {
    if (m.index > last) parts.push({ text: s.slice(last, m.index) });
    if (m[2]) parts.push({ text: m[2], bold: true, italic: true });
    else if (m[3]) parts.push({ text: m[3], bold: true });
    else if (m[4]) parts.push({ text: m[4], code: true });
    else if (m[5]) parts.push({ text: m[5], href: m[6] });
    else if (m[7]) parts.push({ text: m[7], href: m[7] });
    else if (m[9]) {
      if (m[8]) parts.push({ text: m[8] });
      parts.push({ text: m[9], italic: true });
    }
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push({ text: s.slice(last) });
  return parts;
}
